'use client'
import Image from 'next/image'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper'
import { Autoplay, Navigation } from 'swiper/modules'
import { useEffect, useMemo, useRef, useState } from 'react'
import CarouselNavigation from '@/components/common/CarouselNavigation'
import { CURRENT_SUBSIDIARY, PHADKE_SUBSIDIARY } from '@/utils/constants'
import {
  getBannerDimension,
  getBannerImageUrl,
  hasBannerImage,
} from '@/utils/commonFunc'
import { loadState } from '@/utils/localStorage'
import { useSelector } from '@/lib/store'
import { useMediaQuery } from '@/hooks/use-media-query'
import { GET_BANNER_API } from '@/helper/apiConstants'
import { cmsRequest } from '@/helper/request'
import QualityExpertiseSectionSkeleton from '@/components/common/Skeleton/QualityExpertiseSectionSkeleton'

const FALLBACK_SIZE = { width: 420, height: 240 }

const QualityExpertiseSection = ({
  isMobile,
  ssrBannerData,
}: {
  isMobile: boolean
  ssrBannerData?: Record<string, any[]>
}) => {
  const swiperRef = useRef<SwiperType | null>(null)

  const goToNextSlide = () => swiperRef.current?.slideNext()
  const goToPrevSlide = () => swiperRef.current?.slidePrev()

  const hasSsrBanners = useMemo(
    () =>
      Object.values(ssrBannerData || {}).some(
        (banners) => Array.isArray(banners) && banners.length > 0
      ),
    [ssrBannerData]
  )

  const { value: isDesktopLive, mounted } = useMediaQuery('(min-width: 640px)')
  const effectiveIsMobile = mounted ? !isDesktopLive : isMobile

  const [bannersBySubsidiary, setBannersBySubsidiary] = useState<
    Record<string, any[]>
  >(ssrBannerData || {})

  const [isFallbackLoading, setIsFallbackLoading] =
    useState<boolean>(!hasSsrBanners)
  const didFetchRef = useRef(false)

  const [isHydrated, setIsHydrated] = useState(false)

  useEffect(() => {
    setIsHydrated(true)
  }, [])

  const cityState = useSelector((state) => state.city) || {}

  const persistedSubsidiary = isHydrated
    ? loadState('city')?.subsidiary || cityState?.currentCityObj?.subsidiary
    : undefined

  const subsidiaryKey =
    persistedSubsidiary === PHADKE_SUBSIDIARY
      ? PHADKE_SUBSIDIARY
      : CURRENT_SUBSIDIARY

  const bannerCards = useMemo(() => {
    // Drop banners with no usable image rather than rendering <img src="">.
    const preferred = bannersBySubsidiary?.[subsidiaryKey]
    if (Array.isArray(preferred)) {
      const usable = preferred.filter(hasBannerImage)
      if (usable.length > 0) return usable
    }

    const fallback = bannersBySubsidiary?.[CURRENT_SUBSIDIARY]
    return Array.isArray(fallback) ? fallback.filter(hasBannerImage) : []
  }, [bannersBySubsidiary, subsidiaryKey])

  useEffect(() => {
    if (hasSsrBanners || didFetchRef.current) return
    didFetchRef.current = true
    let cancelled = false

    const fetchBannerData = async () => {
      try {
        const apiResponse: any = await cmsRequest(GET_BANNER_API, 'post', {
          section: 'QUALITY_EXPERTISE',
          screen: 'HOME',
          bannerDocumentType: isMobile ? 'MOBILE_WEB' : 'DESKTOP',
        })
        if (!cancelled && apiResponse?.status === true) {
          setBannersBySubsidiary(apiResponse?.data?.bannersBySubsidiary || {})
        }
      } catch (error) {
        console.error('Error fetching banner data:', error)
      } finally {
        if (!cancelled) setIsFallbackLoading(false)
      }
    }

    fetchBannerData()
    return () => {
      cancelled = true
    }
  }, [hasSsrBanners, isMobile])

  if (!hasSsrBanners && isFallbackLoading) {
    return <QualityExpertiseSectionSkeleton />
  }

  if (bannerCards.length === 0) return null

  return (
    <section className="container max-w-full py-4 md:py-8 bg-white">
      {/* Header */}
      <div className="mb-3 sm:mb-5 flex items-center justify-between">
        <h3 className="text-heading-md md:text-heading-lg text-gray-900 mb-0">
          Quality & Expertise
        </h3>
      </div>

      {/* Banner Carousel */}
      <div className="relative">
        {bannerCards.length > 1 && (
          <CarouselNavigation
            goToPrevSlide={goToPrevSlide}
            goToNextSlide={goToNextSlide}
          />
        )}
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          navigation
          slidesPerView="auto"
          spaceBetween={20}
          className="navigation_hidden"
          modules={[Autoplay, Navigation]}
        >
          {bannerCards.map((item: any, index: number) => (
            <SwiperSlide
              key={`slider_${index}`}
              className="h_auto sm:max-w-[420px] w-full sm:w-full"
              style={{ width: '100%' }}
            >
              <Image
                className="h-full w-full rounded-[24px]"
                // Non-empty: bannerCards is filtered by hasBannerImage above.
                src={getBannerImageUrl(item, effectiveIsMobile)}
                alt={
                  typeof item?.bannerSubText === 'string'
                    ? item.bannerSubText
                    : ''
                }
                width={getBannerDimension(item?.width, FALLBACK_SIZE.width)}
                height={getBannerDimension(item?.height, FALLBACK_SIZE.height)}
                sizes="(max-width: 640px) 100vw, 420px"
                priority={index === 0}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
}

export default QualityExpertiseSection
